import React, { useCallback, useEffect, useState } from 'react';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { Perspective } from '@v2/pages/common/enums';
import { font } from '@styles/fonts';
import { getColorBasedOnMachineStatus } from '@helpers';
import EventTimeDisplay from '@v2/pages/common/components/NCFileBatchEvents/components/EventTimeDisplay';
import moment from 'moment';
import TimeDisplayInStaticFormat from '@components/common/TimeDisplayInStaticFormat';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { RawMachineEventData } from '@interfaces/base';
import { retrieveRawMachineEventData } from '@reducers/machinesV2';
import { TraceEventPopupProps } from '@v2/pages/common/components/NCFileBatchEvents/components/TraceEventPopup';
import { getStartBlockText, parseRawMachineEventData } from '@v2/pages/common/components/NCFileBatchEvents/helpers';

interface UseManageTraceEventPopupParams extends Pick<TraceEventPopupProps, 'event' | 'C' | 'onClose'> {}

export const useManageTraceEventPopup = ({ event, C, onClose }: UseManageTraceEventPopupParams) => {
  const dispatch = useAppDispatch();

  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const [rawMachineEventData, setRawMachineEventData] = useState<{ data: RawMachineEventData | null; loading: boolean }>({
    data: null,
    loading: true,
  });

  const { ncFileVersionSequenceNumber: eventVersion, ncFileName, sequenceNumber, machineId } = event;

  useEffect(() => {
    const abortController = new AbortController();

    (async () => {
      try {
        const rawMachineEventResponse = await dispatch(
          retrieveRawMachineEventData(
            {
              machineEventSequenceNumber: sequenceNumber,
              machineId,
            },
            abortController.signal
          )
        );
        if (rawMachineEventResponse?.rawMachineEvents) {
          setRawMachineEventData({ data: rawMachineEventResponse?.rawMachineEvents?.[0], loading: false });
        }
      } catch (e) {
        const err = e as Error;
        setRawMachineEventData({ data: null, loading: false });
        console.error(`Failed to load raw machine event data`, { params: { machineId, sequenceNumber }, error: err });
      }
    })();

    return () => abortController.abort();
  }, [sequenceNumber, machineId]);

  const eventDuration = moment(event.endedOn).diff(moment(event.startedOn), 's');
  const badgeColor = getColorBasedOnMachineStatus(event.status);
  const lineBg = 'Background/Light/Field on modal highlight';

  const onNcFileClick = useCallback(() => {
    onClose();
    navigateToMainDetailsScreen({
      mainScreen: NCFileDetailsScreenName,
      subScreen: NCFileDetailsScreen.NC_FILE,
      params: {
        perspective: Perspective.NC_FILE,
        ncFileName,
        ncFileId: event.ncFileId,
        ncFileStatus: event.ncFileStatus,
      },
    });
  }, [onClose, navigateToMainDetailsScreen, ncFileName, event.ncFileId, event.ncFileStatus]);

  const eventDurationComponent = (
    <TimeDisplayInStaticFormat
      timeValue={eventDuration}
      C={C}
      color={badgeColor}
      fontForDigit={'Time 3'}
      fontForSecondDigit={'Time 4'}
      fontForDelimiter={'Caption 1'}
    />
  );

  const config = [
      {
        borderBottomRadius: false,
        borderTopRadius: true,
        onlyLabel: false,
        loading: false,
        i: {
          name: 'Start',
          isText: false,
          ValueComponent: <EventTimeDisplay timeValue={event.startedOn} C={C} font={font} />,
        },
      },
      {
        borderBottomRadius: false,
        borderTopRadius: false,
        onlyLabel: false,
        loading: false,
        i: {
          name: 'End',
          isText: false,
          ValueComponent: <EventTimeDisplay timeValue={event.endedOn} C={C} font={font} />,
        },
      },
      {
        borderBottomRadius: false,
        borderTopRadius: false,
        onlyLabel: false,
        loading: false,
        i: {
          name: 'Event Duration',
          isText: false,
          ValueComponent: eventDurationComponent,
        },
      },
      {
        borderBottomRadius: false,
        borderTopRadius: false,
        onlyLabel: false,
        loading: false,
        i: {
          name: 'Version',
          isText: true,
          value: eventVersion,
        },
      },
      {
        borderBottomRadius: false,
        borderTopRadius: false,
        onlyLabel: false,
        loading: rawMachineEventData.loading,
        i: {
          name: 'Start Block',
          isText: true,
          value: getStartBlockText(rawMachineEventData.data?.currentBlockNumberRaw ?? null) ,
        },
      },
      {
        borderBottomRadius: true,
        borderTopRadius: false,
        onlyLabel: false,
        loading: rawMachineEventData.loading,
        i: {
          name: 'Count Method',
          isText: true,
          value: rawMachineEventData.data?.machineCountingDetails?.countingMethod,
        },
      },
    ];

    const parsedRawMachineEventData = parseRawMachineEventData(rawMachineEventData.data);

  return { onNcFileClick, ncFileName, eventVersion, config, lineBg, parsedRawMachineEventData };
};
